"use client";

import React, { useEffect, useRef, useState } from "react";
import { Clock, Compass, Handshake } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const features = [{
  title: "Measurable Results",
  description: "We focus on the metrics that actually matter. Our data-driven approach ensures your marketing investment translates directly into leads, revenue, and sustainable business growth.",
  icon: Compass
}, {
  title: "Small Business Focus",
  description: "We specialize in the unique challenges of local service companies. Our strategies are tailored to help small businesses dominate their local markets and build lasting authority.",
  icon: Handshake
}, {
  title: "Strategic Execution",
  description: "Success comes from a well-defined plan, not guesswork. We build comprehensive digital strategies that align perfectly with your specific revenue goals and long-term objectives.",
  icon: Clock
}];
export default function TheKerryMethod() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="92980" id="the-fox-river-advantage" className="relative overflow-hidden bg-[#F6FAFE] py-24 lg:py-32">
      {}
      <div className="pointer-events-none absolute -top-24 -right-24 z-0 opacity-[0.03] text-[#000000]">
        <svg width="400" height="400" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="2" />
          <circle cx="30" cy="50" r="20" stroke="currentColor" strokeWidth="2" />
          <circle cx="70" cy="50" r="20" stroke="currentColor" strokeWidth="2" />
          <circle cx="50" cy="30" r="20" stroke="currentColor" strokeWidth="2" />
          <circle cx="50" cy="70" r="20" stroke="currentColor" strokeWidth="2" />
        </svg>
      </div>

      {}
      <div className="pointer-events-none absolute -bottom-32 -left-32 z-0 opacity-[0.03] text-[#000000]">
        <svg width="500" height="500" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M50 10 L90 50 L50 90 L10 50 Z" stroke="currentColor" strokeWidth="2" />
          <path d="M50 20 L80 50 L50 80 L20 50 Z" stroke="currentColor" strokeWidth="2" />
          <circle cx="50" cy="50" r="20" stroke="currentColor" strokeWidth="2" />
        </svg>
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div ref={headerReveal.ref} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ${headerReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="font-sans text-3xl font-bold tracking-tight text-[#000000] md:text-4xl lg:text-5xl">
            The Fox River Advantage
          </h2>
          <p className="mt-4 text-lg text-[#464646] md:text-xl">
            A strategic, results-oriented approach to scaling your small business online with precision and local expertise.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3 lg:gap-12">
          {features.map((feature, i) => {
          const cardReveal = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.1
          });
          return <div key={feature.title} data-index={i} ref={cardReveal.ref} className={`transition-all duration-1000 delay-[${i * 200}ms] ${cardReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
                <Card className="flex h-full flex-col items-center border-none bg-white text-center shadow-md rounded-none">
                  <CardHeader className="pb-4">
                    <div className="mb-6 flex size-20 items-center justify-center rounded-none bg-[#FF6600]/10 text-[#FF6600] ring-1 ring-[#FF6600]/20">
                      <feature.icon className="size-10 stroke-[1.25]" />
                    </div>
                    <CardTitle className="font-sans text-2xl font-semibold text-[#000000]">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm leading-relaxed text-[#464646]">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}